export const DUMMY_FECHA_PANEL = '2026-08-06'

export function fechaHoyISO() {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Santiago',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date())
  const get = (type) => parts.find((p) => p.type === type)?.value
  return `${get('year')}-${get('month')}-${get('day')}`
}

export const MOTIVO_NUEVO_LABEL = {
  sin_otorgamientos: 'Sin otorgamientos',
  una_operacion: '1 operación histórica',
  inactivo_6m: 'Sin operar en 6 meses',
}

export const STAGE_COMPLETED = {
  Verificación: [],
  Cesión: ['Verificación'],
  'Aceptación SII': ['Verificación', 'Cesión'],
  Finalizada: ['Verificación', 'Cesión', 'Aceptación SII'],
}

export function businessDaysBetween(startISO, endISO) {
  if (!startISO || !endISO) return 0
  const start = new Date(startISO)
  const end = new Date(`${endISO}T23:59:59`)
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return 0
  const d = new Date(start.getFullYear(), start.getMonth(), start.getDate())
  const e = new Date(end.getFullYear(), end.getMonth(), end.getDate())
  let count = 0
  while (d < e) {
    d.setDate(d.getDate() + 1)
    const day = d.getDay()
    if (day !== 0 && day !== 6) count += 1
  }
  return count
}

export function stageDoneSet(stage) {
  return new Set(STAGE_COMPLETED[stage] || [])
}

export const DEFAULT_PRIORIDAD = Object.freeze({
  altoDesde: 4,
  medioDesde: 2,
})

const PRIORIDAD_RANK = { Alto: 3, Medio: 2, Bajo: 1 }

export function prioridadConfig(config) {
  const src = config?.prioridad || config || {}
  const altoDesde = Number(src.altoDesde)
  const medioDesde = Number(src.medioDesde)
  return {
    altoDesde: Number.isFinite(altoDesde) && altoDesde > 0 ? altoDesde : DEFAULT_PRIORIDAD.altoDesde,
    medioDesde:
      Number.isFinite(medioDesde) && medioDesde >= 0 ? medioDesde : DEFAULT_PRIORIDAD.medioDesde,
  }
}

export function prioridadPorDias(days, config) {
  const { altoDesde, medioDesde } = prioridadConfig(config)
  const n = Number(days) || 0
  if (n >= altoDesde) return 'Alto'
  if (n >= medioDesde) return 'Medio'
  return 'Bajo'
}

export function prioridadBadgeClass(priority) {
  const key = String(priority || '').toLowerCase()
  if (key === 'alto') return 'high'
  if (key === 'medio') return 'medium'
  return 'low'
}

export function enrichOperations(rows, fecha, config) {
  const prio = prioridadConfig(config)
  return (Array.isArray(rows) ? rows : [])
    .map((o) => {
      const days = businessDaysBetween(o.createdAt, fecha)
      const priority = prioridadPorDias(days, prio)
      return { ...o, days, priority }
    })
    .sort((a, b) => {
      const rank = (PRIORIDAD_RANK[b.priority] || 0) - (PRIORIDAD_RANK[a.priority] || 0)
      if (rank !== 0) return rank
      return new Date(a.createdAt) - new Date(b.createdAt)
    })
}

export function moneyMM(v) {
  const n = Number(v)
  if (!Number.isFinite(n)) return '$ 0 MM'
  return `$ ${n.toLocaleString('es-CL')} MM`
}

/** Ítems pendientes seleccionables en la tabla de seguimiento (ambos paneles). */
export const PANEL_PENDIENTES_OPTS = Object.freeze([
  { id: 'pendiente_pep', label: 'Pendiente PEP' },
  { id: 'pendiente_uaf', label: 'Pendiente UAF' },
  { id: 'verificacion', label: 'Verificación' },
  { id: 'cesion', label: 'Cesión' },
  { id: 'aceptacion', label: 'Aceptación' },
  { id: 'carta_guia', label: 'Carta guía' },
  { id: 'cheque', label: 'Cheque' },
  { id: 'aprobacion', label: 'Aprobación' },
])

/** Valores antiguos aún presentes en BD; se muestran, no se ofrecen al seleccionar. */
const PANEL_PENDIENTES_LEGACY_LABELS = Object.freeze({
  documentos_legales: 'Documentos legales',
})

export function normalizePanelPendientes(raw) {
  const order = [
    ...PANEL_PENDIENTES_OPTS.map((o) => o.id),
    ...Object.keys(PANEL_PENDIENTES_LEGACY_LABELS),
  ]
  const allowed = new Set(order)
  const aliases = {
    'documentos legales': 'documentos_legales',
    documentos_legales: 'documentos_legales',
    'pendiente pep': 'pendiente_pep',
    pendiente_pep: 'pendiente_pep',
    pep: 'pendiente_pep',
    'pendiente uaf': 'pendiente_uaf',
    pendiente_uaf: 'pendiente_uaf',
    uaf: 'pendiente_uaf',
    verificacion: 'verificacion',
    verificación: 'verificacion',
    cesion: 'cesion',
    cesión: 'cesion',
    aceptacion: 'aceptacion',
    aceptación: 'aceptacion',
    'carta guia': 'carta_guia',
    'carta guía': 'carta_guia',
    carta_guia: 'carta_guia',
    cheque: 'cheque',
    aprobacion: 'aprobacion',
    aprobación: 'aprobacion',
  }
  let items = []
  if (Array.isArray(raw)) items = raw
  else if (raw == null || raw === '') items = []
  else {
    const s = String(raw).trim()
    try {
      const parsed = JSON.parse(s)
      items = Array.isArray(parsed) ? parsed : [parsed]
    } catch {
      items = s.split(',').map((x) => x.trim()).filter(Boolean)
    }
  }
  const seen = new Set()
  for (const item of items) {
    const key = aliases[String(item || '').trim().toLowerCase()]
    if (key && allowed.has(key)) seen.add(key)
  }
  return order.filter((id) => seen.has(id))
}

export function labelPanelPendientes(ids) {
  const map = {
    ...Object.fromEntries(PANEL_PENDIENTES_OPTS.map((o) => [o.id, o.label])),
    ...PANEL_PENDIENTES_LEGACY_LABELS,
  }
  return normalizePanelPendientes(ids)
    .map((id) => map[id] || id)
    .join(', ')
}

export function isDummyPayload(res) {
  return String(res?.meta?.fuente || '').toLowerCase() === 'skeleton'
}

export function filterOperations(rows, query) {
  const q = String(query || '').trim().toLowerCase()
  if (!q) return rows
  return rows.filter(
    (o) =>
      String(o.rut || '').toLowerCase().includes(q) ||
      String(o.cliente || '').toLowerCase().includes(q) ||
      String(o.ejecutivo || '').toLowerCase().includes(q) ||
      String(o.area || o.stage || '').toLowerCase().includes(q) ||
      String(o.nOperacion ?? '').toLowerCase().includes(q),
  )
}
